import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import Input from '../../components/ui/Input';
import FAQCategory from './components/FAQCategory';

const FAQ_STRUCTURE = [
  {
    id: 'general',
    titleKey: 'faq.categories.general',
    questions: [
      { id: 'general-1', q: 'faq.items.general1q', a: 'faq.items.general1a' },
      { id: 'general-2', q: 'faq.items.general2q', a: 'faq.items.general2a' },
      { id: 'general-3', q: 'faq.items.general3q', a: 'faq.items.general3a' },
    ],
  },
  {
    id: 'candidates-upload',
    titleKey: 'faq.categories.candidatesUpload',
    questions: [
      { id: 'upload-1', q: 'faq.items.upload1q', a: 'faq.items.upload1a' },
      { id: 'upload-2', q: 'faq.items.upload2q', a: 'faq.items.upload2a' },
      { id: 'upload-3', q: 'faq.items.upload3q', a: 'faq.items.upload3a' },
    ],
  },
  {
    id: 'ats-sync',
    titleKey: 'faq.categories.atsSync',
    questions: [
      { id: 'ats-1', q: 'faq.items.ats1q', a: 'faq.items.ats1a' },
      { id: 'ats-2', q: 'faq.items.ats2q', a: 'faq.items.ats2a' },
      { id: 'ats-3', q: 'faq.items.ats3q', a: 'faq.items.ats3a' },
      { id: 'ats-4', q: 'faq.items.ats4q', a: 'faq.items.ats4a' },
    ],
  },
  {
    id: 'ai-voice',
    titleKey: 'faq.categories.aiVoice',
    questions: [
      { id: 'ai-1', q: 'faq.items.ai1q', a: 'faq.items.ai1a' },
      { id: 'ai-2', q: 'faq.items.ai2q', a: 'faq.items.ai2a' },
      { id: 'ai-3', q: 'faq.items.ai3q', a: 'faq.items.ai3a' },
    ],
  },
  {
    id: 'billing',
    titleKey: 'faq.categories.billing',
    questions: [
      { id: 'billing-1', q: 'faq.items.billing1q', a: 'faq.items.billing1a' },
      { id: 'billing-2', q: 'faq.items.billing2q', a: 'faq.items.billing2a' },
      { id: 'billing-3', q: 'faq.items.billing3q', a: 'faq.items.billing3a' },
    ],
  },
];

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  const faqData = useMemo(
    () => FAQ_STRUCTURE.map((category) => ({
      id: category.id,
      title: t(category.titleKey),
      questions: category.questions.map((item) => ({
        id: item.id,
        question: t(item.q),
        answer: t(item.a),
      })),
    })),
    [t, i18n.language],
  );

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev?.[categoryId],
    }));
  };

  const filteredFaqData = faqData
    ?.map((category) => ({
      ...category,
      questions: category?.questions?.filter(
        (q) =>
          searchTerm === '' ||
          q?.question?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          q?.answer?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
      ),
    }))
    ?.filter((category) => category?.questions?.length > 0);

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
            {t('faq.title')}
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="max-w-3xl">
          <Input
            type="search"
            placeholder={t('faq.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
        </div>

        <div className="space-y-4 md:space-y-4">
          {filteredFaqData?.length > 0 ? (
            filteredFaqData?.map((category) => (
              <FAQCategory
                key={category?.id}
                category={category}
                isExpanded={expandedCategories?.[category?.id]}
                onToggle={() => toggleCategory(category?.id)}
              />
            ))
          ) : (
            <div className="bg-card rounded-lg border border-border p-8 text-center">
              <p className="text-muted-foreground">{t('faq.empty')}</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default FAQ;
